import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Drawer, IconButton, LinearProgress,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { useUnit } from 'effector-react';

import { reportsApi } from '../api/reports';
import { buildingsApi } from '../api/buildings';
import { $isSettlementTenant, $tenantDataStartDate } from '../store/auth';
import { $complexes, fetchResidentialComplexesFx } from '../store/complexes';
import { getObjectKindLabels } from '../utils/complexKind';
import { buildFileName, downloadBlobAsFile } from '../utils/download';

dayjs.locale('ru');

const surfaceSx = {
  borderRadius: 3,
  border: '1px solid var(--border-color)',
  background: 'var(--surface-bg)',
  boxShadow: 'none',
};

const filterFieldSx = {
  minWidth: 180,
  '& .MuiOutlinedInput-root': { borderRadius: 2.25, backgroundColor: '#FFFFFF' },
};

const stickyCellSx = (left, width, header = false) => ({
  position: 'sticky', left, zIndex: header ? 5 : 2, minWidth: width, width,
  backgroundColor: header ? '#F4F7FB' : '#FFFFFF',
  borderRight: left > 0 ? '1px solid #E5EAF1' : undefined,
});

const statusStyles = {
  ok: { color: '#087A55', backgroundColor: '#E9F8F2', borderColor: '#BCE9D9' },
  missing: { color: '#64748B', backgroundColor: '#F1F5F9', borderColor: '#DCE3EB' },
  no_previous: { color: '#9A5B08', backgroundColor: '#FFF7E5', borderColor: '#F3D8A1' },
  reset: { color: '#B42318', backgroundColor: '#FFF0EE', borderColor: '#F5C2BC' },
  missing_both: { color: '#64748B', backgroundColor: '#F1F5F9', borderColor: '#DCE3EB' },
  missing_current: { color: '#B42318', backgroundColor: '#FFF0EE', borderColor: '#F5C2BC' },
  missing_previous: { color: '#9A5B08', backgroundColor: '#FFF7E5', borderColor: '#F3D8A1' },
};

const StatusBadge = ({ status, label }) => {
  const tone = statusStyles[status] || statusStyles.missing;
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', minHeight: 24, px: 1,
      borderRadius: 999, border: '1px solid', fontSize: 12, fontWeight: 650,
      lineHeight: 1, whiteSpace: 'nowrap', ...tone,
    }}>
      {label || status}
    </Box>
  );
};

const formatNum = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
};

const formatTs = (value) => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : String(value);
};

export default function SettlementElectricityPage() {
  const [isSettlementTenant, complexes, tenantDataStartDate] = useUnit([
    $isSettlementTenant, $complexes, $tenantDataStartDate,
  ]);
  const minDate = useMemo(
    () => (tenantDataStartDate ? dayjs(tenantDataStartDate) : undefined),
    [tenantDataStartDate]
  );
  const labels = useMemo(() => getObjectKindLabels(isSettlementTenant), [isSettlementTenant]);

  const [complexId, setComplexId] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [buildings, setBuildings] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => dayjs().startOf('month'));
  const [dateTo, setDateTo] = useState(() => dayjs());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [hourlyDateFrom, setHourlyDateFrom] = useState(() => dayjs().startOf('day'));
  const [hourlyDateTo, setHourlyDateTo] = useState(() => dayjs().startOf('day'));
  const [hourlyPage, setHourlyPage] = useState(0);
  const [hourlyRowsPerPage, setHourlyRowsPerPage] = useState(100);
  const [hourlyReport, setHourlyReport] = useState(null);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [hourlyExporting, setHourlyExporting] = useState(false);
  const [hourlyError, setHourlyError] = useState('');
  const loadRequestRef = useRef(0);
  const hourlyRequestRef = useRef(0);

  useEffect(() => {
    if (!minDate?.isValid?.()) return;
    if (dateFrom.isBefore(minDate, 'day')) setDateFrom(minDate);
    if (dateTo.isBefore(minDate, 'day')) setDateTo(minDate);
  }, [dateFrom, dateTo, minDate]);

  useEffect(() => { fetchResidentialComplexesFx().catch(() => {}); }, []);

  useEffect(() => {
    if (!complexes?.length) return;
    if (!complexId || !complexes.some((item) => String(item.id) === String(complexId))) {
      setComplexId(String(complexes[0].id));
    }
  }, [complexes, complexId]);

  useEffect(() => {
    if (!complexId) {
      setBuildings([]);
      setBuildingId('');
      return;
    }
    let cancelled = false;
    buildingsApi.getBuildingsByComplexId(complexId)
      .then((data) => {
        if (!cancelled) {
          setBuildings(Array.isArray(data) ? data : []);
          setBuildingId('');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBuildings([]);
          setBuildingId('');
        }
      });
    return () => { cancelled = true; };
  }, [complexId]);

  const queryParams = useMemo(() => {
    if (!complexId || !dateFrom?.isValid() || !dateTo?.isValid()) return null;
    return {
      residentialComplexId: String(complexId),
      dateFrom: dateFrom.format('YYYY-MM-DD'),
      dateTo: dateTo.format('YYYY-MM-DD'),
      buildingId: buildingId || null,
    };
  }, [complexId, dateFrom, dateTo, buildingId]);

  const loadReport = useCallback(async () => {
    if (!queryParams) return;
    if (dateFrom.isAfter(dateTo, 'day')) {
      setError('Дата начала не может быть позже даты конца');
      return;
    }
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const data = await reportsApi.getSettlementElectricityReport(queryParams);
      if (loadRequestRef.current === requestId) setReport(data);
    } catch (err) {
      if (loadRequestRef.current === requestId) {
        setReport(null);
        setError(err?.response?.data?.detail || 'Не удалось загрузить таблицу');
      }
    } finally {
      if (loadRequestRef.current === requestId) setLoading(false);
    }
  }, [queryParams, dateFrom, dateTo]);

  useEffect(() => { if (queryParams) loadReport(); }, [queryParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = async () => {
    if (!queryParams) return;
    setExporting(true);
    setError('');
    try {
      const { data, fileName } = await reportsApi.downloadSettlementElectricityReport(queryParams);
      const fallback = buildFileName(
        ['settlement-electricity', queryParams.dateFrom, queryParams.dateTo],
        'settlement-electricity.xlsx'
      );
      downloadBlobAsFile(data, fileName || fallback);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось выгрузить Excel');
    } finally {
      setExporting(false);
    }
  };

  const openHourlyDetails = (row) => {
    const end = dateTo.isAfter(dayjs(), 'day') ? dayjs() : dateTo;
    let start = dateFrom;
    if (end.diff(start, 'day') > 30) start = end.subtract(30, 'day');
    if (minDate?.isValid?.() && start.isBefore(minDate, 'day')) start = minDate;
    setSelectedDevice(row);
    setHourlyDateFrom(start.startOf('day'));
    setHourlyDateTo(end.startOf('day'));
    setHourlyPage(0);
    setHourlyReport(null);
    setHourlyError('');
  };

  const hourlyQueryParams = useMemo(() => {
    if (!selectedDevice || !complexId || !hourlyDateFrom.isValid() || !hourlyDateTo.isValid()) return null;
    return {
      residentialComplexId: String(complexId),
      deviceId: String(selectedDevice.device_id),
      dateFrom: hourlyDateFrom.format('YYYY-MM-DD'),
      dateTo: hourlyDateTo.format('YYYY-MM-DD'),
      buildingId: selectedDevice.building_id || null,
      skip: hourlyPage * hourlyRowsPerPage,
      limit: hourlyRowsPerPage,
    };
  }, [selectedDevice, complexId, hourlyDateFrom, hourlyDateTo, hourlyPage, hourlyRowsPerPage]);

  const loadHourlyReport = useCallback(async () => {
    if (!hourlyQueryParams) return;
    if (hourlyDateFrom.isAfter(hourlyDateTo, 'day')) {
      setHourlyError('Дата начала не может быть позже даты конца');
      return;
    }
    if (hourlyDateTo.diff(hourlyDateFrom, 'day') > 30) {
      setHourlyError('Выберите период не более 31 дня');
      return;
    }
    const requestId = hourlyRequestRef.current + 1;
    hourlyRequestRef.current = requestId;
    setHourlyLoading(true);
    setHourlyError('');
    try {
      const data = await reportsApi.getSettlementElectricityHourlyReport(hourlyQueryParams);
      if (hourlyRequestRef.current === requestId) setHourlyReport(data);
    } catch (err) {
      if (hourlyRequestRef.current === requestId) {
        setHourlyReport(null);
        setHourlyError(err?.response?.data?.detail || 'Не удалось загрузить почасовые данные');
      }
    } finally {
      if (hourlyRequestRef.current === requestId) setHourlyLoading(false);
    }
  }, [hourlyQueryParams, hourlyDateFrom, hourlyDateTo]);

  useEffect(() => { if (hourlyQueryParams) loadHourlyReport(); }, [hourlyQueryParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHourlyExport = async () => {
    if (!hourlyQueryParams) return;
    setHourlyExporting(true);
    setHourlyError('');
    try {
      const { data, fileName } = await reportsApi.downloadSettlementElectricityHourlyReport(hourlyQueryParams);
      const fallback = buildFileName(
        ['hourly', selectedDevice?.device_serial, hourlyQueryParams.dateFrom, hourlyQueryParams.dateTo],
        'hourly-readings.xlsx'
      );
      downloadBlobAsFile(data, fileName || fallback);
    } catch (err) {
      setHourlyError(err?.response?.data?.detail || 'Не удалось выгрузить Excel');
    } finally {
      setHourlyExporting(false);
    }
  };

  if (!isSettlementTenant) return <Navigate to="/home" replace />;

  const rows = report?.rows || [];
  const hourlyRows = hourlyReport?.rows || [];
  const hourlyCoverage = hourlyReport?.hours_total
    ? Math.round((Number(hourlyReport.hours_received || 0) / Number(hourlyReport.hours_total)) * 100)
    : 0;
  const hourlyEarliestFrom = hourlyDateTo.subtract(30, 'day');
  const hourlyMinFrom = minDate?.isValid?.() && minDate.isAfter(hourlyEarliestFrom, 'day')
    ? minDate
    : hourlyEarliestFrom;
  const hourlyLatestTo = hourlyDateFrom.add(30, 'day');
  const hourlyMaxTo = hourlyLatestTo.isBefore(dayjs(), 'day') ? hourlyLatestTo : dayjs();

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1540, mx: 'auto' }}>
      <Stack spacing={1.5}>
        <Paper sx={{ ...surfaceSx, p: 1.5, background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%)' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap">
              <TextField select label={labels.complex} value={complexId} onChange={(event) => setComplexId(event.target.value)} size="small" sx={{ ...filterFieldSx, minWidth: 220 }}>
                {(complexes || []).map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
              </TextField>
              <TextField select label="Дом" value={buildingId} onChange={(event) => setBuildingId(event.target.value)} size="small" sx={filterFieldSx}>
                <MenuItem value="">Все дома</MenuItem>
                {buildings.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
              </TextField>
              <DatePicker label="С" value={dateFrom} minDate={minDate} onChange={(value) => setDateFrom(value || dayjs().startOf('month'))} format="DD.MM.YYYY" slotProps={{ textField: { size: 'small', sx: { ...filterFieldSx, minWidth: 150 } } }} />
              <DatePicker label="По" value={dateTo} minDate={minDate} maxDate={dayjs()} onChange={(value) => setDateTo(value || dayjs())} format="DD.MM.YYYY" slotProps={{ textField: { size: 'small', sx: { ...filterFieldSx, minWidth: 150 } } }} />
              <Button variant="contained" startIcon={<RefreshRoundedIcon />} onClick={loadReport} disabled={loading || !queryParams} sx={{ borderRadius: 2.25, minHeight: 40, boxShadow: 'none' }}>Показать</Button>
              <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={handleExport} disabled={exporting || loading || !rows.length} sx={{ borderRadius: 2.25, minHeight: 40 }}>Excel</Button>
            </Stack>
          </LocalizationProvider>
        </Paper>

        {error ? <Alert severity="error">{String(error)}</Alert> : null}

        <Paper sx={{ ...surfaceSx, overflow: 'hidden' }}>
          {report && !loading ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} sx={{ px: 2, py: 1.25, borderBottom: '1px solid #E5EAF1' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#173B5E' }}>{dayjs(report.date_from).format('DD.MM.YYYY')} – {dayjs(report.date_to).format('DD.MM.YYYY')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto !important' } }}>{rows.length} приборов · нажмите на строку для почасовых данных</Typography>
            </Stack>
          ) : null}

          {loading ? (
            <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : !rows.length ? (
            <Box sx={{ px: 2, py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Box sx={{ width: 42, height: 42, display: 'grid', placeItems: 'center', mx: 'auto', mb: 1.25, borderRadius: '50%', backgroundColor: '#EDF4FA', color: '#42749D' }}><BoltRoundedIcon fontSize="small" /></Box>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>Нет электросчётчиков за выбранный период</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: '72vh', scrollbarColor: '#B8C5D3 transparent' }}>
              <Table stickyHeader size="small" sx={{
                minWidth: 1320,
                '& .MuiTableCell-head': { py: 1.15, backgroundColor: '#F4F7FB', color: '#43566C', borderBottom: '1px solid #DCE4ED', fontSize: 12, fontWeight: 750, whiteSpace: 'nowrap' },
                '& .MuiTableCell-body': { py: 0.9, borderBottomColor: '#EDF1F5', fontSize: 13, fontVariantNumeric: 'tabular-nums' },
                '& .MuiTableRow-root:hover .MuiTableCell-body': { backgroundColor: '#F5F9FD' },
              }}>
                <TableHead><TableRow>
                  <TableCell sx={stickyCellSx(0, 150, true)}>Дом</TableCell><TableCell sx={stickyCellSx(150, 160, true)}>Прибор</TableCell>
                  <TableCell align="right">День (конец)</TableCell><TableCell align="right">Ночь (конец)</TableCell><TableCell align="right">Общее (конец)</TableCell>
                  <TableCell align="right">День (начало)</TableCell><TableCell align="right">Ночь (начало)</TableCell><TableCell align="right">Общее (начало)</TableCell>
                  <TableCell align="right">Расход день</TableCell><TableCell align="right">Расход ночь</TableCell><TableCell align="right">Расход общее</TableCell>
                  <TableCell>Дата конца</TableCell><TableCell>Дата начала</TableCell><TableCell>Статус</TableCell><TableCell width={46} />
                </TableRow></TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.device_id}
                      hover
                      role="button"
                      tabIndex={0}
                      aria-label={`Открыть почасовые показания прибора ${row.device_serial || ''}`}
                      onClick={() => openHourlyDetails(row)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openHourlyDetails(row);
                        }
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ ...stickyCellSx(0, 150), fontWeight: 700, color: '#173B5E' }}>{row.building_name || '—'}</TableCell>
                      <TableCell sx={{ ...stickyCellSx(150, 160), fontWeight: 650 }}>{row.device_serial || '—'}</TableCell>
                      <TableCell align="right">{formatNum(row.day_end)}</TableCell><TableCell align="right">{formatNum(row.night_end)}</TableCell><TableCell align="right">{formatNum(row.total_end)}</TableCell>
                      <TableCell align="right">{formatNum(row.day_start)}</TableCell><TableCell align="right">{formatNum(row.night_start)}</TableCell><TableCell align="right">{formatNum(row.total_start)}</TableCell>
                      <TableCell align="right">{formatNum(row.day_consumption)}</TableCell><TableCell align="right">{formatNum(row.night_consumption)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 750, color: '#0B6B52' }}>{formatNum(row.total_consumption)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatTs(row.end_ts)}</TableCell><TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatTs(row.start_ts)}</TableCell>
                      <TableCell><StatusBadge status={row.status} label={row.status_label} /></TableCell>
                      <TableCell align="center"><Tooltip title="Почасовые показания"><ChevronRightRoundedIcon sx={{ color: '#5D7690', verticalAlign: 'middle' }} /></Tooltip></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Drawer anchor="right" open={Boolean(selectedDevice)} onClose={() => setSelectedDevice(null)} PaperProps={{ sx: { width: { xs: '100%', md: 'min(980px, 82vw)' }, backgroundColor: '#F5F7FA' } }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, px: { xs: 1.5, md: 2.5 }, py: 1.5, backgroundColor: '#FFFFFF', borderBottom: '1px solid #DDE5ED' }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ width: 38, height: 38, display: 'grid', placeItems: 'center', borderRadius: 2, color: '#1769A6', backgroundColor: '#EAF3FA' }}><BoltRoundedIcon fontSize="small" /></Box>
            <Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 750, color: '#173B5E' }} noWrap>Прибор {selectedDevice?.device_serial || 'без номера'}</Typography><Typography variant="caption" color="text.secondary" noWrap>Дом {selectedDevice?.building_name || '—'} · почасовые показания</Typography></Box>
            <IconButton onClick={() => setSelectedDevice(null)} sx={{ ml: 'auto !important' }} aria-label="Закрыть"><CloseRoundedIcon /></IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}><Stack spacing={1.5}>
          <Paper sx={{ ...surfaceSx, p: 1.5 }}><LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru"><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <DatePicker label="С" value={hourlyDateFrom} minDate={hourlyMinFrom} maxDate={hourlyDateTo} onChange={(value) => { setHourlyDateFrom(value || hourlyDateTo); setHourlyPage(0); }} format="DD.MM.YYYY" slotProps={{ textField: { size: 'small', sx: filterFieldSx } }} />
            <DatePicker label="По" value={hourlyDateTo} minDate={hourlyDateFrom} maxDate={hourlyMaxTo} onChange={(value) => { setHourlyDateTo(value || dayjs()); setHourlyPage(0); }} format="DD.MM.YYYY" slotProps={{ textField: { size: 'small', sx: filterFieldSx } }} />
            <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={handleHourlyExport} disabled={hourlyExporting || hourlyLoading || !hourlyRows.length} sx={{ borderRadius: 2.25, minHeight: 40, ml: { sm: 'auto !important' } }}>Excel</Button>
          </Stack></LocalizationProvider></Paper>

          {hourlyError ? <Alert severity="error">{String(hourlyError)}</Alert> : null}

          {hourlyReport && !hourlyLoading ? (
            <Paper sx={{ ...surfaceSx, position: 'relative', px: 2, py: 1.5 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.25, sm: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center"><ScheduleRoundedIcon sx={{ color: '#1769A6', fontSize: 21 }} /><Box><Typography variant="caption" color="text.secondary">Часы с данными</Typography><Typography sx={{ fontWeight: 750, lineHeight: 1.1 }}>{hourlyReport.hours_received} из {hourlyReport.hours_total}</Typography></Box></Stack>
              <Stack direction="row" spacing={1} alignItems="center"><TaskAltRoundedIcon sx={{ color: hourlyCoverage >= 95 ? '#0B8A60' : hourlyCoverage >= 70 ? '#B7791F' : '#C24135', fontSize: 21 }} /><Box><Typography variant="caption" color="text.secondary">Полнота</Typography><Typography sx={{ fontWeight: 750, lineHeight: 1.1 }}>{hourlyCoverage}%</Typography></Box></Stack>
            </Stack><LinearProgress variant="determinate" value={Math.min(hourlyCoverage, 100)} color={hourlyCoverage >= 95 ? 'success' : hourlyCoverage >= 70 ? 'warning' : 'error'} sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, backgroundColor: '#E9EEF4' }} /></Paper>
          ) : null}

          <Paper sx={{ ...surfaceSx, overflow: 'hidden' }}>
            {hourlyLoading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box> : !hourlyRows.length ? <Box sx={{ p: 6, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">Нет данных за выбранный период</Typography></Box> : (
              <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)', minHeight: 280 }}><Table stickyHeader size="small" sx={{ minWidth: 820, '& .MuiTableCell-head': { py: 1.1, backgroundColor: '#F4F7FB', color: '#43566C', fontSize: 12, fontWeight: 750, whiteSpace: 'nowrap' }, '& .MuiTableCell-body': { py: 0.85, borderBottomColor: '#EDF1F5', fontSize: 13, fontVariantNumeric: 'tabular-nums' } }}>
                <TableHead><TableRow><TableCell>Дата</TableCell><TableCell>Час</TableCell><TableCell align="right">День</TableCell><TableCell align="right">Ночь</TableCell><TableCell align="right">Общее</TableCell><TableCell align="right">Расход за час</TableCell><TableCell>Время пакета</TableCell><TableCell>Статус</TableCell></TableRow></TableHead>
                <TableBody>{hourlyRows.map((row) => (
                  <TableRow key={`${row.device_id}-${row.hour_start}`} sx={row.status === 'missing' ? { backgroundColor: '#FAFBFC' } : undefined}>
                    <TableCell sx={{ fontWeight: 650, whiteSpace: 'nowrap' }}>{dayjs(row.hour_date).format('DD.MM.YYYY')}</TableCell>
                    <TableCell><Box component="span" sx={{ display: 'inline-flex', px: 0.9, py: 0.35, borderRadius: 1.25, backgroundColor: '#EDF4FA', color: '#285D89', fontWeight: 750, whiteSpace: 'nowrap' }}>{row.hour_label}</Box></TableCell>
                    <TableCell align="right">{formatNum(row.day_value)}</TableCell><TableCell align="right">{formatNum(row.night_value)}</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{formatNum(row.total_value)}</TableCell><TableCell align="right" sx={{ fontWeight: 750, color: '#0B6B52' }}>{formatNum(row.total_consumption)}</TableCell><TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatTs(row.reading_ts)}</TableCell><TableCell><StatusBadge status={row.status} label={row.status_label} /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table></TableContainer>
            )}
            {hourlyReport?.total ? <TablePagination component="div" count={hourlyReport.total} page={hourlyPage} onPageChange={(_, value) => setHourlyPage(value)} rowsPerPage={hourlyRowsPerPage} onRowsPerPageChange={(event) => { setHourlyRowsPerPage(Number(event.target.value)); setHourlyPage(0); }} rowsPerPageOptions={[50, 100, 200, 500]} labelRowsPerPage="Строк" labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`} sx={{ borderTop: '1px solid #E5EAF1', color: 'text.secondary' }} /> : null}
          </Paper>
        </Stack></Box>
      </Drawer>
    </Box>
  );
}
